// The public home page (TOM-980), from the approved design: a fixed dark
// palette, Sora and IBM Plex Mono, and an iPad showing Gallivant RP at anchor
// with Neo answering questions. Every reading on the iPad is from her record
// (pages/home/gallivant.ts) through pages/home/homeScene.ts. The palette and
// fonts do not follow the visitor's screen look (the TOM-980 ADR).
import { useId, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth';
import { BetaSignupForm } from '../components/BetaSignupForm';
import { showTryItLink } from '../lib/demo';
import { useClientConfig } from '../lib/useClientConfig';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useMediaQuery } from '../lib/useMediaQuery';
import { FlipTile } from './home/FlipTile';
import { HomeBoat, HomeWave } from './home/HomeBoat';
import { HomeIpad } from './home/HomeIpad';
import { HomeVideo } from './home/HomeVideo';
import { SCENE_HEADLINE } from './home/homeScene';
import { EVERYONE_TILES, HOW_TILES, MEET_NEO_TILES, type TileCopy } from './home/homeTiles';
import { tileHint } from './home/tileFlip';

/** The METS line's appointment address (TOM-980). Support mail stays SUPPORT_EMAIL. */
const METS_CONTACT_EMAIL = 'contact@neomaris.io';

function TileGrid({
  tiles,
  size,
  grid,
}: {
  tiles: readonly TileCopy[];
  size: 'large' | 'small';
  grid: 'neo' | 'everyone' | 'how';
}) {
  return (
    <div className={`home-tiles home-tiles--${grid}`}>
      {tiles.map((t) => (
        <FlipTile
          key={t.id}
          size={size}
          kicker={t.kicker}
          title={t.title}
          picture={t.picture}
          back={
            size === 'large' ? (
              <>
                <span>{t.text}</span>
                {t.ask ? <span className="home-tile__ask">{t.ask}</span> : null}
              </>
            ) : (
              t.text
            )
          }
        />
      ))}
    </div>
  );
}

function SectionHead({ children, hint }: { children: ReactNode; hint: string }) {
  return (
    <div className="home-section-head">
      {children}
      <span className="home-hint">{hint}</span>
    </div>
  );
}

export function Home() {
  useDocumentTitle('Neomaris | Your boat. Understood.');
  const { config } = useClientConfig();
  const auth = useAuth();
  // Try a demo boat (TOM-868): shown when the server has Try it on, to anyone
  // not signed in to an account of their own.
  const tryIt = showTryItLink(config, auth.status, auth.user);
  const heroRef = useRef<HTMLElement>(null);
  const hint = tileHint(!useMediaQuery('(hover: none)'));
  const betaInputId = useId();

  return (
    <main className="home">
      <section className="home-hero" ref={heroRef} aria-labelledby="home-title">
        <HomeBoat />
        <div className="home-wrap home-hero__row">
          <div className="home-hero__copy">
            <span className={`home-pill home-tone--${SCENE_HEADLINE.tone}`}>
              <span className="home-dot" />
              Neo · {SCENE_HEADLINE.text}
            </span>
            <h1 id="home-title" className="home-hero__title">
              Your boat.
              <br />
              Understood.
            </h1>
            <p className="home-hero__lede">
              Meet Neo — an AI crew member that never sleeps. Neo watches over your boat&apos;s
              systems around the clock and answers questions conversationally, whether you&apos;re
              on or off your boat.
            </p>
            <div className="home-hero__cta">
              <Link to="#beta" className="home-btn home-btn--accent home-btn--lg">
                Join the beta
              </Link>
              {tryIt ? (
                <Link
                  to="/try"
                  className="home-btn home-btn--outline home-btn--lg"
                  data-testid="home-try-it"
                >
                  Try a demo boat
                </Link>
              ) : null}
            </div>
          </div>
          <div className="home-hero__device">
            <HomeIpad heroRef={heroRef} />
          </div>
        </div>
        <HomeWave />
      </section>

      <section className="home-band home-band--page home-problem" aria-labelledby="home-problem">
        <div className="home-wrap home-problem__inner">
          <h2 id="home-problem" className="home-h2">
            Your boat is always talking. Neo translates.
          </h2>
        </div>
      </section>

      <section className="home-band home-band--mid" id="neo" aria-labelledby="home-neo">
        <div className="home-wrap home-stack">
          <SectionHead hint={hint}>
            <div className="home-neo-head">
              <h2 id="home-neo" className="home-display">
                Meet Neo
              </h2>
              <h3 className="home-h2">A crew member who knows your boat.</h3>
            </div>
          </SectionHead>
          <TileGrid tiles={MEET_NEO_TILES} size="large" grid="neo" />
        </div>
      </section>

      <section className="home-band home-band--page" aria-labelledby="home-everyone">
        <div className="home-wrap home-stack">
          <SectionHead hint={hint}>
            <h2 id="home-everyone" className="home-h2 home-h2--narrow">
              Useful to everyone aboard.
            </h2>
          </SectionHead>
          <TileGrid tiles={EVERYONE_TILES} size="small" grid="everyone" />
        </div>
      </section>

      <section className="home-band home-band--deep" id="how" aria-labelledby="home-how">
        <div className="home-wrap home-stack">
          <SectionHead hint={hint}>
            <h2 id="home-how" className="home-h2">
              Connect. Learn. Ask.
            </h2>
          </SectionHead>
          <TileGrid tiles={HOW_TILES} size="small" grid="how" />
          <p className="home-note">
            Neo works alongside your existing electronics and alarms. It informs and recommends — it
            doesn&apos;t control vessel systems.
          </p>
        </div>
      </section>

      <HomeVideo />

      <section className="home-band home-band--page" id="beta" aria-labelledby="home-beta">
        <div className="home-wrap home-beta">
          <div className="home-beta__intro">
            <h2 id="home-beta" className="home-h2">
              Bring Neo aboard.
            </h2>
            <p className="home-beta__lede">
              Join the beta and we&apos;ll be in touch about getting your boat connected.
            </p>
            <div className="home-mets">
              <p className="home-mets__title">METS 2026 · Amsterdam · Nov 16–19</p>
              <p className="home-mets__line">
                <a href={`mailto:${METS_CONTACT_EMAIL}`}>Contact us</a> for an appointment
              </p>
            </div>
          </div>
          <div className="home-beta__panel">
            <BetaSignupForm
              variant="home"
              inputId={betaInputId}
              submitLabel="Request beta access"
            />
          </div>
        </div>
      </section>
    </main>
  );
}
